import { GenreSlider } from '@/components/genre-slider';
import { LanguageSelector } from '@/components/language-selector';
import { Button } from '@/components/ui/button';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';
import { Link, usePage } from '@inertiajs/react';
import { Facebook, Instagram, Menu, Settings, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface PublicLayoutProps {
    children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
    const page = usePage();
    const { locale, auth, genres } = page.props as any;
    const { url } = page;
    const { homeUrl, genresUrl, authorsUrl, segments } = useTranslatedRoutes();
    const { t } = useTranslations();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Use the homeUrl from useTranslatedRoutes which handles locale properly
    const homeLink = homeUrl();

    // Check active routes
    const isHome = url === homeLink || url === '/' || url === `/${locale}` || url === `/${locale}/`;
    const isGenres = url.includes(`/${segments.genre}/`) || url.includes(`/${segments.genres}`);
    const isAuthors = url.includes(`/${segments.author}/`) || url.includes(`/${segments.authors}`);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    return (
        <div className="public-theme flex min-h-screen flex-col bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container mx-auto px-4">
                    <div className="flex h-20 items-center justify-between">
                        {/* Left side - Logo and Navigation */}
                        <div className="flex items-center gap-2">
                            {/* Logo */}
                            <Link href={homeLink} className="mr-10 flex items-center">
                                <img src="/logo.svg" alt="Brajti" className="h-10 w-auto" />
                            </Link>

                            {/* Desktop Navigation */}
                            <nav className="hidden items-center gap-2 md:flex">
                                <Link
                                    href={homeLink}
                                    className={cn(
                                        'rounded-lg px-6 py-2.5 text-base font-medium transition-all duration-200',
                                        isHome ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                >
                                    {t('Home')}
                                </Link>
                                <Link
                                    href={genresUrl()}
                                    className={cn(
                                        'rounded-lg px-6 py-2.5 text-base font-medium transition-all duration-200',
                                        isGenres ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                >
                                    {t('Genres')}
                                </Link>
                                <Link
                                    href={authorsUrl()}
                                    className={cn(
                                        'rounded-lg px-6 py-2.5 text-base font-medium transition-all duration-200',
                                        isAuthors ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                >
                                    {t('Authors')}
                                </Link>
                            </nav>
                        </div>

                        {/* Right side - Language Selector */}
                        <div className="hidden md:block">
                            <LanguageSelector />
                        </div>

                        {/* Mobile Navigation */}
                        <div className="flex items-center gap-4 md:hidden">
                            <LanguageSelector />
                            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Navigation Dropdown */}
            {mobileMenuOpen && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40 bg-black/20 md:hidden" onClick={() => setMobileMenuOpen(false)} />

                    {/* Menu */}
                    <div className="fixed top-20 right-0 left-0 z-50 md:hidden">
                        <div className="border-b bg-background shadow-lg">
                            <nav className="container mx-auto space-y-3 px-4 py-6">
                                <Link
                                    href={homeLink}
                                    className={cn(
                                        'block rounded-lg px-6 py-3 text-base font-medium transition-all duration-200',
                                        isHome ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('Home')}
                                </Link>
                                <Link
                                    href={genresUrl()}
                                    className={cn(
                                        'block rounded-lg px-6 py-3 text-base font-medium transition-all duration-200',
                                        isGenres ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('Genres')}
                                </Link>
                                <Link
                                    href={authorsUrl()}
                                    className={cn(
                                        'block rounded-lg px-6 py-3 text-base font-medium transition-all duration-200',
                                        isAuthors ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:bg-primary/5 hover:text-primary',
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('Authors')}
                                </Link>
                            </nav>
                        </div>
                    </div>
                </>
            )}

            {/* Genre Slider */}
            {genres && genres.length > 0 && <GenreSlider genres={genres} />}

            {/* Main Content */}
            <main className="flex-1">{children}</main>

            {/* Footer */}
            <footer className="border-t bg-muted/50">
                <div className="container mx-auto px-4 py-12">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
                        {/* CTA - First on mobile, last on desktop */}
                        <div className="order-1 md:order-3 md:col-span-4">
                            <h3 className="mb-2 font-semibold">{t('Join')}</h3>
                            <p className="mb-4 text-sm text-muted-foreground">
                                {t('Start the quiz and join the Brajti community. Get tailored book picks in minutes.')}
                            </p>
                            <Button asChild>
                                <a
                                    href={`https://brajti.com/quiz/brajti_gender?sub=true&utm_source=blog_brajti&cc=${locale}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full"
                                >
                                    {t('Start quiz')}
                                </a>
                            </Button>
                        </div>

                        {/* Logo and Company Info - Second on mobile, first on desktop */}
                        <div className="order-2 md:order-1 md:col-span-4">
                            <Link href={homeLink} className="mb-4 inline-block">
                                <img src="/logo.svg" alt="Brajti" className="h-8 w-auto" />
                            </Link>
                            <p className="mb-4 text-sm text-muted-foreground">{t('© VEBOTEK OÜ. All rights reserved.')}</p>

                            {/* Social Media Links */}
                            <div className="items-left mb-4 flex gap-4">
                                <a
                                    href="https://www.facebook.com/profile.php?id=61572166540243"
                                    className="inline-flex items-center gap-2 text-muted-foreground transition-opacity hover:opacity-80"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Facebook"
                                >
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                                        <Facebook className="h-5 w-5" />
                                    </span>
                                    <span className="text-sm">Facebook</span>
                                </a>
                                <a
                                    href="https://www.instagram.com/brajti/"
                                    className="inline-flex items-center gap-2 text-muted-foreground transition-opacity hover:opacity-80"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Instagram"
                                >
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                                        <Instagram className="h-5 w-5" />
                                    </span>
                                    <span className="text-sm">Instagram</span>
                                </a>
                            </div>

                            {auth?.user && (
                                <Link
                                    href="/admin/dashboard"
                                    className="inline-flex items-center text-sm text-muted-foreground transition-colors hover:text-primary"
                                >
                                    <Settings className="mr-1 h-4 w-4" />
                                    {t('Admin Panel')}
                                </Link>
                            )}
                        </div>

                        {/* Links - Third on mobile, second on desktop */}
                        <div className="order-3 md:order-2 md:col-span-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Link
                                        href={`/${locale}/about-us`}
                                        className="mb-2 block text-sm text-muted-foreground transition-colors hover:text-primary"
                                    >
                                        {t('About Us')}
                                    </Link>
                                    <a
                                        href="https://brajti.com/privacy.html?utm_source=blog_brajti"
                                        className="mb-2 block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Privacy Policy')}
                                    </a>
                                    <a
                                        href="https://brajti.com/support.html?utm_source=blog_brajti"
                                        className="mb-2 block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Support')}
                                    </a>
                                    <a
                                        href="https://brajti.com/contact-us.html?utm_source=blog_brajti"
                                        className="block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Contact Us')}
                                    </a>
                                </div>
                                <div>
                                    <a
                                        href="https://brajti.com/terms.html?utm_source=blog_brajti"
                                        className="mb-2 block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Terms and Conditions')}
                                    </a>
                                    <a
                                        href="https://brajti.com/subscription-terms.html?utm_source=blog_brajti"
                                        className="mb-2 block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Subscription Terms')}
                                    </a>
                                    <a
                                        href="https://brajti.com/imprint.html?utm_source=blog_brajti"
                                        className="block text-sm text-muted-foreground transition-colors hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Imprint')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
